import { ClinicMerchantAccountSettingsService } from './../../../services/clinic-merchant-account-settings.service';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MerchantDevice } from './../../../models/finance/merchant-device';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { ConfirmDeleteDialogComponent } from '@app/management/dialogs/confirm-delete/confirm-delete.component';
import { GenericDialogComponent } from '@app/management/dialogs/generic-confirm/generic-confirm.component';
import { Clinic } from '@models/clinic';
import { ClinicsService } from '@services/clinics.service';
import { MerchantDeviceService } from '@services/merchant-device.service';
import { Subject, throwError } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ClinicMerchantAccountSettings } from '@models/payments/clinic-merchant-account-settings';
import { ClinicConvergeMerchant } from '@models/payments/clinic-converge-merchant';
import { ClinicConvergeMerchantService } from '@services/clinic-converge-merchant.service';

@Component({
  selector: 'app-org-merchant-devices',
  templateUrl: './org-merchant-devices.component.html',
  styleUrls: ['./org-merchant-devices.component.less'],
})
export class OrgMerchantDevicesComponent implements OnInit, OnDestroy {
  disableGrid = false;
  loading = false;
  gridData: any[];
  unsub = new Subject<any>();
  clinicId: number;
  clinic: Clinic;

  accountSettingsFormGroup = new FormGroup({
    chain: new FormControl(null, [Validators.required]),
    location: new FormControl(null, [Validators.required]),
    terminal: new FormControl(null, [Validators.required]),
  });

  onlinePaymentAccountSettingsFormGroup = new FormGroup({
    merchantId: new FormControl('', [Validators.required]),
    merchantUserId: new FormControl('', [Validators.required]),
    merchantPinCode: new FormControl('', [Validators.required]),
  });

  currentClinicMerchantAccountSettings: ClinicMerchantAccountSettings;
  currentClinicConvergeMerchant: ClinicConvergeMerchant;

  constructor(
    private merchantDeviceService: MerchantDeviceService,
    private clinicsService: ClinicsService,
    private router: Router,
    private dialog: MatDialog,
    private clinicMerchantAccountSettingsService: ClinicMerchantAccountSettingsService,
    private clinicConvergeMerchantService: ClinicConvergeMerchantService
  ) {}

  ngOnInit() {
    this.clinicsService.clinicIdSelected$
      .asObservable()
      .pipe(takeUntil(this.unsub))
      .subscribe((clinicId) => {
        this.clinicId = clinicId;
        this.getMerchantDevices();
        this.clinicsService.getClinicById(this.clinicId).subscribe((snapshot) => {
          this.clinic = snapshot as Clinic;
          this.disableOrEnableMerchantDeviceSettings();
          this.disableOrEnableOnlinePaymentAccountSettings();
        });
      });

    this.merchantDeviceService.merchantDeviceUpdateSource$.pipe(takeUntil(this.unsub)).subscribe((res) => {
      if (res) {
        this.getMerchantDevices();
      }
      this.disableGrid = false;
    });

    this.loading = true;
    this.clinicMerchantAccountSettingsService
      .getClinicMerchantAccountSettings()
      .pipe(takeUntil(this.unsub))
      .subscribe((settings) => {
        settings ??= {
          id: 0,
          chain: '',
          location: '',
          terminal: '',
        };
        this.currentClinicMerchantAccountSettings = structuredClone(settings);
        this.accountSettingsFormGroup.patchValue(settings);
        this.loading = false;
      });

    this.loading = true;
    this.clinicConvergeMerchantService
      .getClinicConvergeMerchant()
      .pipe(takeUntil(this.unsub))
      .subscribe((settings) => {
        settings ??= {
          id: 0,
          merchantId: '',
          merchantUserId: '',
          merchantPinCode: '',
        };
        this.currentClinicConvergeMerchant = structuredClone(settings);
        this.onlinePaymentAccountSettingsFormGroup.patchValue(settings);
        this.loading = false;
      });
  }

  private getMerchantDevices() {
    this.loading = true;

    this.merchantDeviceService.getMerchantDevices().subscribe((res) => {
      this.gridData = res;
      this.loading = false;
    });
  }

  public onAddClick({ sender }) {
    this.disableGrid = true;
    this.merchantDeviceService.shareMerchantDevice(null);
    this.router.navigate([
      '/management/organization/clinics/merchant-devices',
      { outlets: { 'action-panel': ['edit-merchant-device', '_'] } },
    ]);
  }

  public editHandler({ sender, rowIndex, dataItem }) {
    this.disableGrid = true;
    this.merchantDeviceService.shareMerchantDevice(dataItem);
    this.router.navigate([
      '/management/organization/clinics/merchant-devices',
      { outlets: { 'action-panel': ['edit-merchant-device', dataItem.id] } },
    ]);
  }

  public removeHandler({ dataItem }) {
    const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, {
      width: '250px',
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === 'delete') {
        this.loading = true;
        this.merchantDeviceService.deleteMerchantDevice(dataItem).subscribe(
          (res) => {
            this.getMerchantDevices();
          },
          (error) => {
            this.loading = false;
            if (error.error && error.message) {
              this.dialog.open(GenericDialogComponent, {
                maxWidth: '500px',
                data: {
                  showCancel: false,
                  title: 'Removal Failed',
                  content: error.message,
                  confirmButtonText: 'Ok',
                },
              });
            } else return throwError(error);
          }
        );
      }
    });
  }

  toggleMerchantDevices() {
    this.clinicsService.updateClinicMerchantDevicesSettings(this.clinic).subscribe(() => {});
    this.disableOrEnableMerchantDeviceSettings();
  }

  disableOrEnableMerchantDeviceSettings() {
    if (!this.clinic.isUsingMerchantDevices) {
      this.accountSettingsFormGroup.disable();
    } else {
      this.accountSettingsFormGroup.enable();
    }
  }
  onSaveMerchantDeviceSettings() {
    const clinicMerchantAccountSettings: ClinicMerchantAccountSettings = this.accountSettingsFormGroup.value;
    clinicMerchantAccountSettings.id = this.currentClinicMerchantAccountSettings.id;
    this.loading = true;
    if (!clinicMerchantAccountSettings.id) {
      this.clinicMerchantAccountSettingsService
        .addClinicMerchantAccountSettings(clinicMerchantAccountSettings)
        .subscribe((addedSettings) => {
          this.accountSettingsFormGroup.patchValue(addedSettings);
          this.accountSettingsFormGroup.markAsPristine();
          this.loading = false;
        });
    } else {
      this.clinicMerchantAccountSettingsService
        .updateClinicMerchantAccountSettings(clinicMerchantAccountSettings)
        .subscribe((updatedSettings) => {
          this.accountSettingsFormGroup.patchValue(updatedSettings);
          this.accountSettingsFormGroup.markAsPristine();
          this.loading = false;
        });
    }
    this.accountSettingsFormGroup.pristine;
  }

  onCancelMerchantDeviceSettings() {
    this.accountSettingsFormGroup.patchValue(this.currentClinicMerchantAccountSettings);
    this.accountSettingsFormGroup.markAsPristine();
  }

  toggleOnlinePaymentAccountSettings() {
    this.clinicsService.updateClinicMerchantDevicesSettings(this.clinic).subscribe(() => {});
    this.disableOrEnableOnlinePaymentAccountSettings();
  }

  disableOrEnableOnlinePaymentAccountSettings() {
    if (!this.clinic.isUsingOnlinePayments) {
      this.onlinePaymentAccountSettingsFormGroup.disable();
    } else {
      this.onlinePaymentAccountSettingsFormGroup.enable();
    }
  }

  onSaveOnlinePaymentAccountSettings() {
    const onlinePaymentAccountSettings: ClinicConvergeMerchant = this.onlinePaymentAccountSettingsFormGroup.value;
    onlinePaymentAccountSettings.id = this.currentClinicConvergeMerchant.id;
    this.loading = true;
    if (!onlinePaymentAccountSettings.id) {
      this.clinicConvergeMerchantService
        .addClinicConvergeMerchant(onlinePaymentAccountSettings)
        .subscribe((addedSettings) => {
          this.onlinePaymentAccountSettingsFormGroup.patchValue(addedSettings);
          this.onlinePaymentAccountSettingsFormGroup.markAsPristine();
          this.loading = false;
        });
    } else {
      this.clinicConvergeMerchantService
        .updateClinicConvergeMerchant(onlinePaymentAccountSettings)
        .pipe(takeUntil(this.unsub))
        .subscribe({
          next: () => {
            this.loading = false;
          },
        });
    }
    this.onlinePaymentAccountSettingsFormGroup.pristine;
  }

  onCancelOnlinePaymentAccountSettings() {
    this.onlinePaymentAccountSettingsFormGroup.patchValue(this.currentClinicConvergeMerchant);
    this.onlinePaymentAccountSettingsFormGroup.markAsPristine();
  }

  ngOnDestroy() {
    this.unsub.next();
    this.unsub.complete();
  }
}
